import React, { useEffect, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Database, FileText, Upload, Info, ArrowRight, AlertTriangle, XCircle, Loader2, ChevronRight, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { Input } from '../components/SubscriptionAppInput';
import { Card } from '../components/SubscriptionAppCard';
// --- Types ---
type Step = 1 | 2 | 3 | 4;
type Platform = 'Recharge' | 'WineHub' | 'Commerce7' | 'WineDirect' | 'Shopify Native' | 'CSV Upload';
interface FieldMapping {
  source: string;
  target: string;
  status: 'mapped' | 'unmapped';
  skipped: boolean;
}
interface PreviewRow {
  id: number;
  email: string;
  name: string;
  plan: string;
  status: 'Active' | 'Paused' | 'Cancelled';
  joinDate: string;
  nextBilling: string;
  validation: 'valid' | 'warning' | 'error';
  message?: string;
}
// --- Mock Data ---
const PLATFORMS: {
  id: Platform;
  description: string;
  icon: React.ElementType;
}[] = [{
  id: 'Recharge',
  description: 'Shopify subscription app',
  icon: Database
}, {
  id: 'WineHub',
  description: 'Wine club management platform',
  icon: Database
}, {
  id: 'Commerce7',
  description: 'Wine DTC platform',
  icon: Database
}, {
  id: 'WineDirect',
  description: 'Wine eCommerce platform',
  icon: Database
}, {
  id: 'Shopify Native',
  description: 'Shopify built-in subscriptions',
  icon: Database
}, {
  id: 'CSV Upload',
  description: 'Upload a spreadsheet',
  icon: FileText
}];
const MOCK_MAPPINGS: FieldMapping[] = [{
  source: 'customer_email',
  target: 'Email',
  status: 'mapped',
  skipped: false
}, {
  source: 'first_name',
  target: 'First Name',
  status: 'mapped',
  skipped: false
}, {
  source: 'last_name',
  target: 'Last Name',
  status: 'mapped',
  skipped: false
}, {
  source: 'created_at',
  target: 'Join Date',
  status: 'mapped',
  skipped: false
}, {
  source: 'subscription_plan',
  target: 'Subscription Plan',
  status: 'mapped',
  skipped: false
}, {
  source: 'status',
  target: 'Status',
  status: 'mapped',
  skipped: false
}, {
  source: 'next_billing',
  target: 'Next Billing Date',
  status: 'mapped',
  skipped: false
}, {
  source: 'price',
  target: 'Price',
  status: 'mapped',
  skipped: false
}, {
  source: 'phone',
  target: 'Phone',
  status: 'mapped',
  skipped: false
}, {
  source: 'loyalty_points',
  target: '',
  status: 'unmapped',
  skipped: false
}, {
  source: 'custom_field_1',
  target: '',
  status: 'unmapped',
  skipped: false
}, {
  source: 'referral_code',
  target: '',
  status: 'unmapped',
  skipped: false
}, {
  source: 'internal_notes',
  target: '',
  status: 'unmapped',
  skipped: false
}, {
  source: 'legacy_id',
  target: '',
  status: 'unmapped',
  skipped: false
}];
const TARGET_FIELDS = ['Email', 'First Name', 'Last Name', 'Phone', 'Subscription Plan', 'Status', 'Join Date', 'Next Billing Date', 'Billing Interval', 'Price', 'Payment Method', 'Shipping Address', 'Tags', 'Notes'];
const PREVIEW_DATA: PreviewRow[] = [{
  id: 1,
  email: 'sarah.j@example.com',
  name: 'Sarah Johnson',
  plan: 'Reserve Club',
  status: 'Active',
  joinDate: '2023-01-15',
  nextBilling: '2026-02-15',
  validation: 'valid'
}, {
  id: 2,
  email: 'mike.smith@test.com',
  name: 'Mike Smith',
  plan: 'Red Only',
  status: 'Active',
  joinDate: '2022-11-01',
  nextBilling: '2026-02-01',
  validation: 'warning',
  message: 'Email already exists in WinePik — will merge'
}, {
  id: 3,
  email: '',
  name: 'Unknown User',
  plan: 'Club 6',
  status: 'Paused',
  joinDate: '2024-03-10',
  nextBilling: '2026-03-10',
  validation: 'error',
  message: 'Missing required field: Email'
}, {
  id: 4,
  email: 'emma.w@winery.com',
  name: 'Emma Williams',
  plan: 'Reserve Club',
  status: 'Active',
  joinDate: '2023-05-20',
  nextBilling: '2026-02-20',
  validation: 'valid'
}, {
  id: 5,
  email: 'david.b@example.org',
  name: 'David Brown',
  plan: 'White Only',
  status: 'Cancelled',
  joinDate: '2021-08-15',
  nextBilling: '-',
  validation: 'valid'
}];
export function MigrationWizardPage() {
  const navigate = useNavigate();
  // --- State ---
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null); // Mock file
  const [migrationName, setMigrationName] = useState('');
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>(MOCK_MAPPINGS);
  const [previewFilter, setPreviewFilter] = useState<'All' | 'Valid' | 'Warnings' | 'Errors'>('All');
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'merge' | 'overwrite'>('skip');
  const [testImport, setTestImport] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importLogs, setImportLogs] = useState<string[]>([]);
  // --- Handlers ---
  const handlePlatformSelect = (platform: Platform) => {
    setSelectedPlatform(platform);
    const date = new Date().toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
    setMigrationName(`${platform} Subscriber Import — ${date}`);
  };
  const handleFileUpload = () => {
    // Simulate file upload
    const mockFile = new File([''], 'recharge_export_jan2026.csv', {
      type: 'text/csv'
    });
    setUploadedFile(mockFile);
  };
  const handleRemoveFile = () => {
    setUploadedFile(null);
  };
  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1 as Step);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1 as Step);
    }
  };
  const handleStartImport = () => {
    setIsImporting(true);
    setImportProgress(0);
    setImportLogs([]);
    // Simulate import process
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 5;
      if (progress > 100) progress = 100;
      setImportProgress(Math.round(progress));
      // Add mock logs
      if (progress > 10 && importLogs.length === 0) {
        setImportLogs(prev => [...prev, 'sarah.j@example.com — imported']);
      }
      if (progress > 30 && importLogs.length === 1) {
        setImportLogs(prev => [...prev, 'emma.w@winery.com — imported']);
      }
      if (progress > 60 && importLogs.length === 2) {
        setImportLogs(prev => [...prev, 'duplicate skipped: mike.smith@test.com']);
      }
      if (progress > 90 && importLogs.length === 3) {
        setImportLogs(prev => [...prev, 'david.b@example.org — imported']);
      }
      if (progress === 100) {
        clearInterval(interval);
        setTimeout(() => {
          navigate('/management/migration');
        }, 1000);
      }
    }, 200);
  };
  // --- Render Helpers ---
  const renderStepper = () => {
    const steps = [{
      num: 1,
      label: 'Source'
    }, {
      num: 2,
      label: 'Map Fields'
    }, {
      num: 3,
      label: 'Preview'
    }, {
      num: 4,
      label: 'Import'
    }];
    return <div className="flex items-center justify-center max-w-2xl mx-auto" data-id="element-7320">
        {steps.map((step, index) => {
        const isActive = currentStep === step.num;
        const isCompleted = currentStep > step.num;
        const isFuture = currentStep < step.num;
        return <Fragment key={step.num} data-id="element-7321">
              {/* Step node */}
              <div className="flex flex-col items-center relative z-10" data-id="element-7322">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-semibold transition-all duration-200 ${isCompleted ? 'bg-[#10B981] text-white shadow-[0_0_0_3px_rgba(16,185,129,0.15)]' : isActive ? 'bg-[#0EA5E9] text-white shadow-[0_0_0_4px_rgba(14,165,233,0.15)]' : 'bg-[#F1F5F9] text-[#94A3B8] border border-[#E2E8F0]'}`} data-id="element-7323">
                  {isCompleted ? <Check className="w-4 h-4" strokeWidth={2.5} data-id="element-7324" /> : step.num}
                </div>
                <span className={`mt-2 text-[12px] whitespace-nowrap ${isActive ? 'font-semibold text-[#0F172A]' : isCompleted ? 'font-medium text-[#475569]' : 'font-normal text-[#94A3B8]'}`} data-id="element-7325">
                  {step.num}. {step.label}
                </span>
              </div>

              {/* Connecting line between steps */}
              {index < steps.length - 1 && <div className="flex-1 mx-3 mt-[-18px]" data-id="element-7326">
                  <div className={`h-[2px] w-full rounded-full transition-colors duration-200 ${currentStep > step.num + 1 ? 'bg-[#10B981]' : currentStep > step.num ? 'bg-[#0EA5E9]' : 'bg-[#E2E8F0]'}`} data-id="element-7327" />
                </div>}
            </Fragment>;
      })}
      </div>;
  };
  // --- Step Content Renderers ---
  const renderStep1 = () => <div className="max-w-4xl mx-auto space-y-8" data-id="element-7328">
      <div className="text-center" data-id="element-7329">
        <h2 className="text-2xl font-bold text-[#0F172A]" data-id="element-7330">
          Where are you migrating from?
        </h2>
        <p className="text-[#64748B] mt-2" data-id="element-7331">
          Select your source platform or upload a CSV file.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-7332">
        {PLATFORMS.map(platform => {
        const isSelected = selectedPlatform === platform.id;
        const Icon = platform.icon;
        return <div key={platform.id} onClick={() => handlePlatformSelect(platform.id)} className={`relative p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${isSelected ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-7333">
              {isSelected && <div className="absolute top-3 right-3 text-[#0EA5E9]" data-id="element-7334">
                  <CheckCircle className="w-5 h-5" data-id="element-7335" />
                </div>}
              <div className="w-10 h-10 rounded-lg bg-[#F1F5F9] flex items-center justify-center mb-3" data-id="element-7336">
                <Icon className="w-5 h-5 text-[#64748B]" data-id="element-7337" />
              </div>
              <h3 className="font-semibold text-[#0F172A]" data-id="element-7338">{platform.id}</h3>
              <p className="text-xs text-[#64748B] mt-1" data-id="element-7339">
                {platform.description}
              </p>
            </div>;
      })}
      </div>

      {selectedPlatform && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-7340">
          {selectedPlatform !== 'CSV Upload' && <div className="bg-[#E0F2FE] border border-[#7DD3FC] rounded-lg p-4 flex items-start gap-3" data-id="element-7341">
              <Info className="w-5 h-5 text-[#0284C7] flex-shrink-0 mt-0.5" data-id="element-7342" />
              <div className="text-sm text-[#0369A1]" data-id="element-7343">
                <p data-id="element-7344">
                  Export your subscriber data from{' '}
                  <strong data-id="element-7345">{selectedPlatform}</strong> as a CSV file.
                </p>
                <button className="flex items-center gap-1 font-medium mt-1 hover:underline" data-id="element-7346">
                  View our export guide for {selectedPlatform}{' '}
                  <ArrowRight className="w-3 h-3" data-id="element-7347" />
                </button>
              </div>
            </div>}

          {!uploadedFile ? <div onClick={handleFileUpload} className="border-2 border-dashed border-[#CBD5E1] rounded-lg p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:border-[#0EA5E9] hover:bg-[#F8FAFC] transition-colors" data-id="element-7348">
              <div className="w-12 h-12 rounded-full bg-[#F1F5F9] flex items-center justify-center mb-4" data-id="element-7349">
                <Upload className="w-6 h-6 text-[#64748B]" data-id="element-7350" />
              </div>
              <h4 className="text-[#0F172A] font-medium mb-1" data-id="element-7351">
                Drag and drop your CSV file here
              </h4>
              <p className="text-sm text-[#64748B]" data-id="element-7352">
                or click to browse. Supports .csv and .xlsx files up to 10MB.
              </p>
            </div> : <Card padding="sm" className="flex items-center justify-between" data-id="element-7353">
              <div className="flex items-center gap-4" data-id="element-7354">
                <div className="w-10 h-10 rounded-lg bg-[#F1F5F9] flex items-center justify-center" data-id="element-7355">
                  <FileText className="w-5 h-5 text-[#64748B]" data-id="element-7356" />
                </div>
                <div data-id="element-7357">
                  <p className="font-medium text-[#0F172A]" data-id="element-7358">
                    {uploadedFile.name}
                  </p>
                  <p className="text-xs text-[#64748B]" data-id="element-7359">
                    2.4 MB • 847 rows, 14 columns detected
                  </p>
                </div>
              </div>
              <button onClick={handleRemoveFile} className="text-sm font-medium text-[#EF4444] hover:text-[#DC2626]" data-id="element-7360">
                Remove
              </button>
            </Card>}

          <Input label="Migration Name" placeholder="e.g. Recharge Subscriber Import — January 2026" value={migrationName} onChange={e => setMigrationName(e.target.value)} data-id="element-7361" />
        </div>}
    </div>;
  const renderStep2 = () => <div className="max-w-5xl mx-auto space-y-6" data-id="element-7362">
      <div className="flex items-center justify-between" data-id="element-7363">
        <div data-id="element-7364">
          <h2 className="text-2xl font-bold text-[#0F172A]" data-id="element-7365">
            Map your columns
          </h2>
          <p className="text-[#64748B] mt-1" data-id="element-7366">
            Match your source columns to WinePik fields.
          </p>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 flex items-center gap-2 text-sm" data-id="element-7367">
        <Info className="w-4 h-4 text-[#64748B]" data-id="element-7368" />
        <span className="text-[#0F172A]" data-id="element-7369">
          14 source columns detected. 9 auto-mapped.
        </span>
        <span className="font-medium text-[#F59E0B]" data-id="element-7370">5 need review</span>
      </div>

      <Card padding="none" className="divide-y divide-[#E2E8F0]" data-id="element-7371">
        {fieldMappings.map((mapping, idx) => <div key={idx} className="flex items-center gap-4 p-4 hover:bg-[#F8FAFC] transition-colors" data-id="element-7372">
            <div className="w-1/3 flex items-center justify-end gap-3" data-id="element-7373">
              <span className="font-mono text-sm bg-[#F1F5F9] px-2 py-1 rounded text-[#475569]" data-id="element-7374">
                {mapping.source}
              </span>
            </div>

            <div className="flex-shrink-0 text-[#94A3B8]" data-id="element-7375">
              <ArrowRight className="w-4 h-4" data-id="element-7376" />
            </div>

            <div className="w-1/3" data-id="element-7377">
              <div className="relative" data-id="element-7378">
                <select className={`w-full h-10 pl-3 pr-8 border rounded-md text-sm appearance-none bg-white focus:outline-none focus:ring-2 ${mapping.status === 'mapped' ? 'border-[#CBD5E1] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-[#0EA5E9]/20' : 'border-[#F59E0B] text-[#0F172A] focus:border-[#F59E0B] focus:ring-[#F59E0B]/20'}`} value={mapping.target} onChange={e => {
              const newMappings = [...fieldMappings];
              newMappings[idx].target = e.target.value;
              newMappings[idx].status = e.target.value ? 'mapped' : 'unmapped';
              setFieldMappings(newMappings);
            }} disabled={mapping.skipped} data-id="element-7379">
                  <option value="" data-id="element-7380">Select field...</option>
                  {TARGET_FIELDS.map(field => <option key={field} value={field} data-id="element-7381">
                      {field}
                    </option>)}
                </select>
                {mapping.status === 'mapped' && !mapping.skipped && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#10B981]" data-id="element-7382">
                    <CheckCircle className="w-4 h-4" data-id="element-7383" />
                  </div>}
              </div>
            </div>

            <div className="flex-1 flex items-center justify-end gap-2" data-id="element-7384">
              <span className="text-xs text-[#64748B]" data-id="element-7385">Skip</span>
              <button onClick={() => {
            const newMappings = [...fieldMappings];
            newMappings[idx].skipped = !newMappings[idx].skipped;
            setFieldMappings(newMappings);
          }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${mapping.skipped ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7386">
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${mapping.skipped ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-7387" />
              </button>
            </div>
          </div>)}
      </Card>

      <div className="bg-[#E0F2FE] border border-[#7DD3FC] rounded-lg p-4" data-id="element-7388">
        <h4 className="text-sm font-semibold text-[#0369A1] mb-1" data-id="element-7389">
          Why Join Date matters
        </h4>
        <p className="text-sm text-[#0369A1]" data-id="element-7390">
          Mapping your original join date preserves member tenure. Without it,
          all imported members start as Month 0 in cohort analytics — losing
          their history.
        </p>
      </div>
    </div>;
  const renderStep3 = () => {
    const filteredData = previewFilter === 'All' ? PREVIEW_DATA : PREVIEW_DATA.filter(row => {
      if (previewFilter === 'Valid') return row.validation === 'valid';
      if (previewFilter === 'Warnings') return row.validation === 'warning';
      if (previewFilter === 'Errors') return row.validation === 'error';
      return true;
    });
    return <div className="max-w-6xl mx-auto space-y-6" data-id="element-7391">
        <div data-id="element-7392">
          <h2 className="text-2xl font-bold text-[#0F172A]" data-id="element-7393">
            Preview your import
          </h2>
          <p className="text-[#64748B] mt-1" data-id="element-7394">
            Review how your data will appear in WinePik.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-7395">
          <Card padding="md" data-id="element-7396">
            <p className="text-xs font-semibold text-[#10B981] uppercase mb-1" data-id="element-7397">
              Ready to Import
            </p>
            <p className="text-3xl font-bold text-[#0F172A]" data-id="element-7398">831</p>
            <p className="text-xs text-[#64748B] mt-1" data-id="element-7399">
              records passed validation
            </p>
          </Card>
          <Card padding="md" data-id="element-7400">
            <p className="text-xs font-semibold text-[#F59E0B] uppercase mb-1" data-id="element-7401">
              Warnings
            </p>
            <p className="text-3xl font-bold text-[#0F172A]" data-id="element-7402">12</p>
            <p className="text-xs text-[#64748B] mt-1" data-id="element-7403">records need review</p>
          </Card>
          <Card padding="md" data-id="element-7404">
            <p className="text-xs font-semibold text-[#EF4444] uppercase mb-1" data-id="element-7405">
              Errors
            </p>
            <p className="text-3xl font-bold text-[#0F172A]" data-id="element-7406">4</p>
            <p className="text-xs text-[#64748B] mt-1" data-id="element-7407">
              records will be skipped
            </p>
          </Card>
        </div>

        <div className="flex gap-2 border-b border-[#E2E8F0]" data-id="element-7408">
          {['All (847)', 'Valid (831)', 'Warnings (12)', 'Errors (4)'].map(tab => {
          const label = tab.split(' ')[0] as any;
          const isActive = previewFilter === label;
          return <button key={tab} onClick={() => setPreviewFilter(label)} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${isActive ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-7409">
                  {tab}
                </button>;
        })}
        </div>

        <Card padding="none" className="overflow-hidden" data-id="element-7410">
          <div className="overflow-x-auto" data-id="element-7411">
            <table className="w-full text-sm text-left" data-id="element-7412">
              <thead className="bg-[#F8FAFC] text-[#64748B] border-b border-[#E2E8F0]" data-id="element-7413">
                <tr data-id="element-7414">
                  <th className="px-4 py-3 w-12 text-xs font-semibold uppercase tracking-wider" data-id="element-7415">
                    #
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" data-id="element-7416">
                    Email
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" data-id="element-7417">
                    Name
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" data-id="element-7418">
                    Plan
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" data-id="element-7419">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" data-id="element-7420">
                    Join Date
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" data-id="element-7421">
                    Next Billing
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider" data-id="element-7422">
                    Validation
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]" data-id="element-7423">
                {filteredData.map(row => <tr key={row.id} className="hover:bg-[#F8FAFC]" data-id="element-7424">
                    <td className="px-4 py-3 text-[#94A3B8]" data-id="element-7425">{row.id}</td>
                    <td className="px-4 py-3 font-medium text-[#0F172A]" data-id="element-7426">
                      {row.email || <span className="text-[#EF4444] italic" data-id="element-7427">Missing</span>}
                    </td>
                    <td className="px-4 py-3 text-[#0F172A]" data-id="element-7428">{row.name}</td>
                    <td className="px-4 py-3 text-[#64748B]" data-id="element-7429">{row.plan}</td>
                    <td className="px-4 py-3" data-id="element-7430">
                      <Badge variant={row.status === 'Active' ? 'success' : row.status === 'Paused' ? 'warning' : 'error'} data-id="element-7431">
                        {row.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-[#64748B]" data-id="element-7432">{row.joinDate}</td>
                    <td className="px-4 py-3 text-[#64748B]" data-id="element-7433">
                      {row.nextBilling}
                    </td>
                    <td className="px-4 py-3 flex justify-center" data-id="element-7434">
                      {row.validation === 'valid' && <CheckCircle className="w-5 h-5 text-[#10B981]" data-id="element-7435" />}
                      {row.validation === 'warning' && <div className="group relative" data-id="element-7436">
                          <AlertTriangle className="w-5 h-5 text-[#F59E0B] cursor-help" data-id="element-7437" />
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-[#1E293B] text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-7438">
                            {row.message}
                          </div>
                        </div>}
                      {row.validation === 'error' && <div className="group relative" data-id="element-7439">
                          <XCircle className="w-5 h-5 text-[#EF4444] cursor-help" data-id="element-7440" />
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-[#1E293B] text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-7441">
                            {row.message}
                          </div>
                        </div>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>

        <Card data-id="element-7442">
          <h3 className="font-semibold text-[#0F172A] mb-3" data-id="element-7443">
            Duplicate Handling
          </h3>
          <div className="space-y-3" data-id="element-7444">
            {[{
            id: 'skip',
            label: 'Skip duplicates',
            desc: "If an email already exists, don't import that row"
          }, {
            id: 'merge',
            label: 'Merge',
            desc: 'Update existing records with new data, keep existing fields where source is blank'
          }, {
            id: 'overwrite',
            label: 'Overwrite',
            desc: 'Replace existing records entirely with imported data'
          }].map(option => <label key={option.id} className="flex items-start gap-3 cursor-pointer" data-id="element-7445">
                <input type="radio" name="duplicateHandling" className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" checked={duplicateHandling === option.id} onChange={() => setDuplicateHandling(option.id as any)} data-id="element-7446" />
                <div data-id="element-7447">
                  <span className="block text-sm font-medium text-[#0F172A]" data-id="element-7448">
                    {option.label}
                  </span>
                  <span className="block text-xs text-[#64748B]" data-id="element-7449">
                    {option.desc}
                  </span>
                </div>
              </label>)}
          </div>
        </Card>
      </div>;
  };
  const renderStep4 = () => <div className="max-w-3xl mx-auto space-y-6" data-id="element-7450">
      <div className="text-center" data-id="element-7451">
        <h2 className="text-2xl font-bold text-[#0F172A]" data-id="element-7452">Ready to import</h2>
        <p className="text-[#64748B] mt-1" data-id="element-7453">
          Review the summary below and start your migration.
        </p>
      </div>

      <Card className="space-y-6" data-id="element-7454">
        {!isImporting ? <>
            <div className="flex items-start justify-between pb-6 border-b border-[#E2E8F0]" data-id="element-7455">
              <div data-id="element-7456">
                <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-7457">
                  {migrationName || 'Untitled Migration'}
                </h3>
                <div className="flex items-center gap-2 mt-2" data-id="element-7458">
                  <Badge variant="info" data-id="element-7459">{selectedPlatform}</Badge>
                  <span className="text-sm text-[#64748B]" data-id="element-7460">
                    {uploadedFile?.name}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6" data-id="element-7461">
              <div data-id="element-7462">
                <p className="text-xs font-medium text-[#64748B] uppercase mb-1" data-id="element-7463">
                  Total Records
                </p>
                <p className="text-xl font-bold text-[#0F172A]" data-id="element-7464">847</p>
              </div>
              <div data-id="element-7465">
                <p className="text-xs font-medium text-[#64748B] uppercase mb-1" data-id="element-7466">
                  Will Import
                </p>
                <p className="text-xl font-bold text-[#10B981]" data-id="element-7467">831</p>
              </div>
              <div data-id="element-7468">
                <p className="text-xs font-medium text-[#64748B] uppercase mb-1" data-id="element-7469">
                  Skipped
                </p>
                <p className="text-sm text-[#64748B]" data-id="element-7470">
                  4 errors, 12 warnings resolved
                </p>
              </div>
              <div data-id="element-7471">
                <p className="text-xs font-medium text-[#64748B] uppercase mb-1" data-id="element-7472">
                  Config
                </p>
                <p className="text-sm text-[#0F172A]" data-id="element-7473">
                  {duplicateHandling === 'skip' ? 'Skip duplicates' : duplicateHandling === 'merge' ? 'Merge duplicates' : 'Overwrite duplicates'}
                  , 9/14 fields
                </p>
              </div>
            </div>

            <div className="pt-6 border-t border-[#E2E8F0]" data-id="element-7474">
              <label className="flex items-start gap-3 cursor-pointer" data-id="element-7475">
                <input type="checkbox" checked={testImport} onChange={e => setTestImport(e.target.checked)} className="mt-1 rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-7476" />
                <div data-id="element-7477">
                  <span className="block text-sm font-medium text-[#0F172A]" data-id="element-7478">
                    Run test import first (imports first 10 records only)
                  </span>
                  <span className="block text-xs text-[#64748B] mt-1" data-id="element-7479">
                    Recommended for your first migration. You can review the
                    test results before importing everything.
                  </span>
                </div>
              </label>
            </div>
          </> : <div className="py-8 text-center space-y-6" data-id="element-7480">
            <div className="relative w-20 h-20 mx-auto" data-id="element-7481">
              <div className="absolute inset-0 border-4 border-[#E2E8F0] rounded-full" data-id="element-7482"></div>
              <div className="absolute inset-0 border-4 border-[#0EA5E9] rounded-full border-t-transparent animate-spin" data-id="element-7483"></div>
              <div className="absolute inset-0 flex items-center justify-center font-bold text-[#0EA5E9]" data-id="element-7484">
                {importProgress}%
              </div>
            </div>

            <div data-id="element-7485">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-7486">
                Importing... {Math.round(831 * importProgress / 100)} of 831
                records
              </h3>
              <p className="text-sm text-[#64748B] mt-1" data-id="element-7487">~3 min remaining</p>
            </div>

            <div className="w-full bg-[#F1F5F9] rounded-full h-2 overflow-hidden max-w-md mx-auto" data-id="element-7488">
              <div className="bg-[#0EA5E9] h-2 rounded-full transition-all duration-300" style={{
            width: `${importProgress}%`
          }} data-id="element-7489" />
            </div>

            <div className="max-w-md mx-auto bg-[#F8FAFC] rounded-lg p-4 text-left space-y-2 h-32 overflow-y-auto border border-[#E2E8F0]" data-id="element-7490">
              {importLogs.map((log, idx) => <div key={idx} className="flex items-center gap-2 text-xs text-[#64748B]" data-id="element-7491">
                  {log.includes('skipped') ? <AlertCircle className="w-3 h-3 text-[#F59E0B]" data-id="element-7492" /> : <Check className="w-3 h-3 text-[#10B981]" data-id="element-7493" />}
                  {log}
                </div>)}
            </div>
          </div>}
      </Card>
    </div>;
  return <div className="bg-[#F1F5F9] min-h-full flex flex-col" data-id="element-7494">
      {/* Top Bar */}
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]" data-id="element-7495">
        {renderStepper()}
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 pb-24" data-id="element-7496">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 md:left-[240px] bg-white border-t border-[#E2E8F0] px-6 py-4 flex items-center justify-between z-10" data-id="element-7497">
        <Button variant="ghost" onClick={() => navigate('/management/migration')} data-id="element-7498">
          Cancel
        </Button>

        <div className="flex items-center gap-3" data-id="element-7499">
          {currentStep > 1 && !isImporting && <Button variant="secondary" onClick={handleBack} data-id="element-7500">
              Back
            </Button>}

          {!isImporting && <Button variant="secondary" onClick={() => console.log('Save Draft')} data-id="element-7501">
              Save Draft
            </Button>}

          {!isImporting && <Button variant="primary" onClick={currentStep === 4 ? handleStartImport : handleNext} disabled={currentStep === 1 && (!selectedPlatform || !uploadedFile)} iconRight={currentStep === 4 ? <Upload className="w-4 h-4" data-id="element-7503" /> : <ChevronRight className="w-4 h-4" data-id="element-7504" />} data-id="element-7502">
              {currentStep === 4 ? 'Start Import' : 'Next Step'}
            </Button>}
        </div>
      </div>
    </div>;
}